/**
 * Conexão do Canva da empresa, usada pelas propostas do CRM.
 *
 * Dois caminhos: MCP (recomendado — funciona em qualquer plano e permite
 * trocar textos {{chave}} num design) e Connect API (integração registrada
 * no Developer Portal). Quem conecta é o dono/admin; o token fica no servidor.
 */
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Palette, CheckCircle2, XCircle, RefreshCw, Loader2, Unplug, ExternalLink, Info, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "wouter";

export default function CanvaPanel() {
  const { can } = usePermissions();
  const podeGerenciar = can("crm:canva_manage");
  const utils = trpc.useUtils();
  const status = trpc.canva.status.useQuery();
  const verify = trpc.canva.verify.useMutation({
    onSuccess: (r) => { toast.success(r.tools ? `Conexão OK — ${r.tools} ferramentas disponíveis` : "Conexão com o Canva OK"); utils.canva.status.invalidate(); },
    onError: (e) => { toast.error(toUserFriendlyError(e, "Falha ao verificar")); utils.canva.status.invalidate(); },
  });
  const disconnect = trpc.canva.disconnect.useMutation({
    onSuccess: () => { toast.success("Canva desconectado"); utils.canva.status.invalidate(); },
    onError: (e) => toast.error(toUserFriendlyError(e, "Falha ao desconectar")),
  });

  const s = status.data;
  const goMcp = () => { window.location.href = "/api/integrations/canva-mcp/connect"; };
  const goConnect = () => { window.location.href = "/api/integrations/canva/connect"; };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Palette className="h-4 w-4 text-primary" /> Canva
        </CardTitle>
        <CardDescription>
          Conecte o Canva da empresa: gere propostas a partir de um design seu com campos <code className="text-xs">{"{{chave}}"}</code>,
          edite qualquer proposta no Canva e traga o PDF pronto de volta.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {status.isLoading || !s ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : !s.connected ? (
          <div className="space-y-3">
            {!s.mcpConfigured && !s.connectConfigured ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 text-sm text-amber-900 dark:text-amber-200 flex gap-2">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <span>A integração com o Canva ainda não está configurada neste ambiente (<code className="text-xs">PUBLIC_BASE_URL</code>).</span>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Nenhuma conta conectada. Quem conectar precisa ter acesso, no Canva, aos designs e templates da empresa.
              </p>
            )}
            {podeGerenciar ? (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  {s.mcpConfigured && (
                    <Button onClick={goMcp} disabled={!s.mcpAllowlisted} title={s.mcpAllowlisted ? undefined : "Aguardando o Canva liberar este domínio"}>
                      <Palette className="h-4 w-4 mr-1.5" /> Conectar Canva (MCP)
                    </Button>
                  )}
                  {s.connectConfigured && (
                    <Button variant={s.mcpAllowlisted ? "outline" : "default"} onClick={goConnect} title="Integração registrada no Developer Portal do Canva">
                      Conectar via Connect API
                    </Button>
                  )}
                </div>
                {s.mcpConfigured && !s.mcpAllowlisted && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 text-xs text-amber-900 dark:text-amber-200 space-y-1">
                    <div className="flex gap-2"><Info className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>
                        A conexão por MCP (a que permite gerar de um design com <code>{"{{chaves}}"}</code>) depende de o Canva liberar o domínio deste
                        servidor — hoje eles só aceitam <code>localhost</code> sem aprovação. Peça a liberação no{" "}
                        <a className="underline font-medium" href={s.mcpWaitlistUrl} target="_blank" rel="noreferrer">formulário do Canva</a>{" "}
                        informando o redirect <code className="break-all">{s.mcpRedirectUri}</code>.
                      </span>
                    </div>
                    <p className="pl-6">
                      Enquanto isso, a Connect API cobre “Abrir no Canva” e “Trazer do Canva” (e Autofill no Enterprise).
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Peça a um administrador para conectar.</p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {s.lastVerifyStatus === "failed" ? <XCircle className="h-4 w-4 text-red-600" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                  <span className="font-medium text-sm">{s.displayName ? `Conectado como ${s.displayName}` : "Conectado"}</span>
                  <Badge variant="outline" className="text-[10px]">{s.transport === "mcp" ? "MCP" : "Connect API"}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {s.connectedBy?.name ? `Conectado por ${s.connectedBy.name} · ` : ""}
                  {new Date(s.connectedAt).toLocaleDateString("pt-BR")}
                  {s.lastUsedAt ? ` · último uso ${new Date(s.lastUsedAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}` : ""}
                </p>
                {s.lastVerifyStatus === "failed" && s.lastVerifyError && (
                  <p className="text-xs text-red-700 dark:text-red-300">{s.lastVerifyError}</p>
                )}
              </div>
              {podeGerenciar && (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" disabled={verify.isPending} onClick={() => verify.mutate()}>
                    {verify.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Verificar
                  </Button>
                  <Button size="sm" variant="outline" onClick={s.transport === "mcp" ? goMcp : goConnect}>Reconectar</Button>
                  {s.transport !== "mcp" && s.mcpConfigured && s.mcpAllowlisted && (
                    <Button size="sm" variant="outline" onClick={goMcp} title="Troca a conexão para MCP (libera designs com {{chave}})">Migrar para MCP</Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-destructive" disabled={disconnect.isPending} onClick={() => disconnect.mutate()}>
                    <Unplug className="h-3.5 w-3.5 mr-1.5" /> Desconectar
                  </Button>
                </div>
              )}
            </div>

            <div className="rounded-md border p-3 space-y-2">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">O que dá para fazer</div>
              <div className="flex flex-wrap gap-2">
                <Badge variant={s.hasPlaceholders ? "secondary" : "outline"} className={s.hasPlaceholders ? "" : "opacity-60 line-through"}>
                  <Sparkles className="h-3 w-3 mr-1" /> Gerar de um design com {"{{chaves}}"}
                </Badge>
                <Badge variant="secondary">Abrir proposta no Canva</Badge>
                <Badge variant="secondary">Trazer PDF do Canva</Badge>
                <Badge variant={s.hasBrandTemplates ? "secondary" : "outline"} className={s.hasBrandTemplates ? "" : "opacity-60 line-through"}>Brand templates</Badge>
                <Badge variant={s.hasAutofill === false ? "outline" : "secondary"} className={s.hasAutofill === false ? "opacity-60 line-through" : ""}>
                  Autofill{s.hasAutofill === null ? " (Enterprise — só sabemos ao usar)" : ""}
                </Badge>
              </div>
              {s.transport === "mcp" ? (
                <p className="text-xs text-muted-foreground">
                  Monte a proposta no Canva com textos como <code>{"{{cliente_organizacao}}"}</code> e <code>{"{{negociacao_valor}}"}</code>; em Propostas → Modelos, escolha
                  “Design com chaves” e aponte para esse design. Cada proposta vira uma cópia dele com os textos trocados.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Pela Connect API não dá para trocar textos num design. Para isso, use “Migrar para MCP”. Autofill é recurso do <strong>Canva Enterprise</strong>.
                </p>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              Os botões aparecem na ficha de cada proposta em{" "}
              <Link href="/crm/proposals" className="underline inline-flex items-center gap-0.5">CRM → Propostas <ExternalLink className="h-3 w-3" /></Link>.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
